/**
 * The puzzle of the day, and the streak that comes with it.
 *
 * Which puzzle it is follows from the date alone, so everybody gets the same
 * one on the same day and the server is never asked. The date is the player's
 * own local date rather than UTC: "today" means today where they are, and a
 * streak that breaks at midnight in a timezone nobody lives in is a bug people
 * feel rather than report.
 *
 * The streak lives in this browser. It is deliberately forgiving in one
 * direction and strict in the other: solving yesterday and then today extends
 * it, solving twice in a day does not, and missing a day ends it. Nothing here
 * can be gamed by changing the clock forward -- a date in the future simply
 * becomes that day's puzzle -- but nothing tries very hard to stop that either,
 * because the streak is a reason to come back, not a score anyone else sees.
 */
import { PUZZLES } from "./puzzles.js";
import type { Puzzle } from "./puzzles.js";

const KEY = "xq-daily";

/** The local date as YYYY-MM-DD. Not toISOString, which is UTC. */
export function today(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The day before a YYYY-MM-DD, by arithmetic rather than string surgery. */
function dayBefore(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(y!, (m ?? 1) - 1, d ?? 1);
  t.setDate(t.getDate() - 1);
  return today(t);
}

/**
 * A 32-bit hash of the date string.
 *
 * Not `daysSinceEpoch % length`: that walks the corpus in order, so consecutive
 * days would serve near-identical positions -- the generator emits them in
 * runs of one kind. Hashing scatters them.
 */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** The puzzle for a given local date. Same answer for everyone, every time. */
export function puzzleFor(iso: string = today()): Puzzle {
  return PUZZLES[hash(iso) % PUZZLES.length]!;
}

export interface DailyState {
  /** the last date solved, or "" */
  last: string;
  streak: number;
  best: number;
  /** how many days have ever been solved */
  total: number;
}

const EMPTY: DailyState = { last: "", streak: 0, best: 0, total: 0 };

export function dailyState(): DailyState {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<DailyState> | null;
    if (!raw || typeof raw !== "object") return { ...EMPTY };
    return {
      last: typeof raw.last === "string" ? raw.last : "",
      streak: Number(raw.streak) || 0,
      best: Number(raw.best) || 0,
      total: Number(raw.total) || 0,
    };
  } catch {
    return { ...EMPTY };
  }
}

function save(s: DailyState): void {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private window */ }
}

/** Has today's already been solved? */
export function solvedToday(now: string = today()): boolean {
  return dailyState().last === now;
}

/**
 * Record a solve for today. Idempotent: a second call on the same day changes
 * nothing, so a double-tap or a re-render cannot inflate a streak.
 */
export function recordDailySolve(now: string = today()): DailyState {
  const s = dailyState();
  if (s.last === now) return s;
  const next: DailyState = {
    last: now,
    streak: s.last === dayBefore(now) ? s.streak + 1 : 1,
    best: 0,
    total: s.total + 1,
  };
  next.best = Math.max(s.best, next.streak);
  save(next);
  return next;
}

/**
 * The streak as it stands *today*, which is not always the stored number: a
 * streak that ended yesterday is over, and should read zero rather than keep
 * showing the number it reached until the player happens to solve again.
 */
export function liveStreak(now: string = today()): number {
  const s = dailyState();
  if (!s.last) return 0;
  return s.last === now || s.last === dayBefore(now) ? s.streak : 0;
}
